// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import {
    FeedbackAuthenticationType,
    FeedbackHostPlatformType,
    FeedbackUiType,
    IFeedbackConfig,
    InAppFeedbackScenarioType,
    FeedbackAgeGroup,
    FeedbackScreenshotSourceType,
    FeedbackScreenshotInfoImageFormat,
    IFeedbackTelemetry,
    IFeedbackInitOptionsWithInitAndPolicies,
    FeedbackType,
    IFeedbackUserData,
} from '@ms/inapp-feedback-loader';
import Bowser from 'bowser';
import { isNonEmptyString, ProfileTheme } from '../utilities';

export class FeedbackNotAllowedError extends Error {}

export enum InAppFeedbackErrorKind {
    Initialization = 'initialization',
    Submit = 'submit',
}

// JSON string containing metadata sent to feedback provider
export type FeedbackMetadata = string;
export const DefaultScenario = InAppFeedbackScenarioType.Experience;
export const DefaultFeedbackUiType = FeedbackUiType.Modal;

export interface IFeedbackLaunchParams {
    theme: ProfileTheme;
    metadataJson?: FeedbackMetadata;
    scenario?: InAppFeedbackScenarioType;
    userData?: IFeedbackUserData;
}

export interface IInAppFeedbackInitData {
    hostName: string;
    appId: number;
    isProduction: boolean;
    locale: string;
    userAgent?: string;
    onError?: (error: Error, errorKind: InAppFeedbackErrorKind) => void;
    onDismiss?: (isFeedbackSent?: boolean) => void;
    onInitializationComplete?: () => void;
    onSuccess?: () => void;
}

export function createFeedbackInitOptions(initData: IInAppFeedbackInitData): IFeedbackInitOptionsWithInitAndPolicies {
    const telemetry: IFeedbackTelemetry = {
        browser: 'unknown',
        browserVersion: 'unknown',
        deviceType: 'unknown',
        platform: 'unknown',
    };

    if (initData.userAgent !== undefined) {
        const bowser = Bowser.getParser(initData.userAgent);
        telemetry.browser = bowser.getBrowserName() || telemetry.browser;
        const version = bowser.getBrowserVersion();
        telemetry.browserVersion = isNonEmptyString(version) ? version : telemetry.browserVersion;
        telemetry.deviceType = bowser.getPlatformType() || telemetry.deviceType;
        telemetry.platform = bowser.getOSName() || telemetry.platform;
    }

    return {
        authenticationType: FeedbackAuthenticationType.Unauthenticated,
        ageGroup: FeedbackAgeGroup.NotAdult,
        appId: initData.appId,
        isProduction: initData.isProduction,
        callbackFunctions: {
            onDismiss: initData.onDismiss,
            onSuccess: initData.onSuccess,
            onError: (errorMessage?: string) => {
                initData.onError?.(new Error(errorMessage ?? 'Unknown error'), InAppFeedbackErrorKind.Submit);
            },
            initializationComplete: initData.onInitializationComplete,
        },
        telemetry,
        feedbackConfig: {
            ...baseFeedbackConfig,
            complianceChecks: undefined,
            scenarioConfig: {
                scenarioType: DefaultScenario,
                isScenarioEnabled: true,
            },
            hostPlatform: FeedbackHostPlatformType.Web,
        },
        tenantId: undefined,
    };
}

export function getBaseOCVTheme(theme: ProfileTheme): string {
    switch (theme) {
        case 'light':
        case 'hc-light':
            return 'M365Light';
        default:
            return 'M365Dark';
    }
}

/*
    Returns special configurations for AI thumbs up / down scenarios.
    Aligned with https://www.owiki.ms/wiki/OCV/Collecting_Copilot_Feedback#UI_recommendations
*/
export function getFeedbackConfigOverrides(scenario?: InAppFeedbackScenarioType): IFeedbackConfig {
    switch (scenario) {
        case InAppFeedbackScenarioType.AIThumbsUp:
            return getThumbsUpConfigOverrides();
        case InAppFeedbackScenarioType.AIThumbsDown:
            return getThumbsDownConfigOverrides();
        default:
            return getDefaultConfigOverrides();
    }
}

function getThumbsUpConfigOverrides(): IFeedbackConfig {
    return {
        ...getSharedAIConfigOverrides(),
        initialFeedbackType: FeedbackType.Smile,
        isShareContextDataEnabled: false,
    };
}

function getThumbsDownConfigOverrides(): IFeedbackConfig {
    return {
        ...getSharedAIConfigOverrides(),
        initialFeedbackType: FeedbackType.Frown,
        isShareContextDataEnabled: true,
    };
}

function getSharedAIConfigOverrides(): IFeedbackConfig {
    return {
        isDisplayed: true,
        isScreenshotEnabled: false,
        isScreenRecordingEnabled: false,
        isEmailCollectionEnabled: false,
        isFeedbackForumEnabled: false,
        isFileUploadEnabled: false,
        isShareContextDataEnabled: false,
        supportUrl: undefined,
    };
}

function getDefaultConfigOverrides(): IFeedbackConfig {
    return {
        initialFeedbackType: undefined,
        isDisplayed: true,
        isScreenshotEnabled: true,
        isFileUploadEnabled: true,
        isScreenRecordingEnabled: false,
        isEmailCollectionEnabled: true,
        isFeedbackForumEnabled: false,
        isShareContextDataEnabled: false,
        supportUrl: undefined,
    };
}

export const baseFeedbackConfig: IFeedbackConfig = {
    email: '',
    feedbackForumUrl: 'https://community.vscodeedu.com',
    feedbackInsightsConfig: {
        isFeedbackInsightsEnabled: false,
    },
    feedbackUiType: DefaultFeedbackUiType,
    hostPlatform: FeedbackHostPlatformType.Web,
    isDisplayed: true,
    isEmailCollectionEnabled: true,
    isFeedbackForumEnabled: false,
    isFileUploadEnabled: true,
    isMyFeedbackEnabled: false,
    invokeOnDismissOnEsc: true,
    isScreenshotEnabled: true,
    isThankYouPageDisabled: false,
    myFeedbackUrl: 'https://feedbackportal.microsoft.com/feedback/mycontent/',
    privacyUrl: 'https://go.microsoft.com/fwlink/?LinkId=521839',
    retentionDurationDays: 30,
    screenshotInfo: {
        providedScreenshotType: FeedbackScreenshotSourceType.AutoCaptureWithHtml2Canvas,
        screenshotImageFormat: FeedbackScreenshotInfoImageFormat.PNG,
    },
    msInternalTitleTarget: 'VSCodeEdu',
    isSupportEnabled: false,
    displayAdditionalInformationEnabled: false,

    scenarioConfig: {
        scenarioType: DefaultScenario,
        isScenarioEnabled: true,
    },

    tenantFeedbackServiceOptions: {
        useTenantFeedbackService: false,
    },
};
